"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface Chapter {
  id: string;
  label: string;
}

interface ChapterDotsProps {
  chapters: Chapter[];
  className?: string;
}

export function ChapterDots({ chapters, className = "" }: ChapterDotsProps) {
  const [activeChapter, setActiveChapter] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      chapters.forEach((chapter, index) => {
        const element = document.getElementById(chapter.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const elementBottom = elementTop + rect.height;

          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveChapter(index);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, [chapters]);

  const scrollToChapter = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
      });
    }
  };

  return (
    <motion.nav
      className={`fixed right-6 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-3 ${className}`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1, duration: 0.5 }}
    >
      {chapters.map((chapter, index) => (
        <button
          key={chapter.id}
          onClick={() => scrollToChapter(chapter.id)}
          className="group flex items-center gap-3 py-1"
          aria-label={`Go to ${chapter.label}`}
        >
          {/* Label (appears on hover) */}
          <span className="text-xs uppercase tracking-widest text-[#DAD5D0]/0 group-hover:text-[#DAD5D0]/60 transition-all duration-300 whitespace-nowrap translate-x-2 group-hover:translate-x-0">
            {chapter.label}
          </span>

          {/* Dot */}
          <motion.div
            className="relative w-2 h-2"
            animate={{
              scale: activeChapter === index ? 1.5 : 1,
            }}
            transition={{ duration: reducedMotion ? 0 : 0.2 }}
          >
            <div
              className={`w-full h-full rounded-full transition-colors duration-300 ${
                activeChapter === index
                  ? "bg-[#DAD5D0]"
                  : "bg-[#DAD5D0]/30 group-hover:bg-[#DAD5D0]/60"
              }`}
            />

            {/* Active ring */}
            {activeChapter === index && (
              <motion.div
                className="absolute inset-0 rounded-full border border-[#DAD5D0]/40"
                initial={{ scale: 1, opacity: 1 }}
                animate={
                  reducedMotion
                    ? {}
                    : {
                        scale: [1, 2],
                        opacity: [0.6, 0],
                      }
                }
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
              />
            )}
          </motion.div>
        </button>
      ))}
    </motion.nav>
  );
}

export default ChapterDots;
